import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { POLIGONO } from "../data/poligono";
import type { ChapterData } from "../data/content-types";
import type { InteractableDef } from "../core/types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CASO EXTRA — "Peso muerto". Almacén logístico: nave, estanterías, palés y carretilla. */
export class PoligonoScene extends WalkChapter {
  protected chapter: ChapterData = POLIGONO;
  protected grid = { w: 15, h: 12 };
  protected spawn = { x: 7, y: 10 };
  protected worldSigns = [
    { id: "m_casco", x: 1.4, y: 0.3 },
    { id: "m_botas", x: 4.5, y: 0.3 },
    { id: "w_atrapamiento", x: 8.5, y: 0.3 },
    { id: "e_salida", x: 13.4, y: 0.3 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("poligono");
  }

  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 16 === 0 ? "dirt" : "concrete";
  }

  /** Arte propio del almacén para cada interactivo. */
  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    switch (def.id) {
      case "carga": return [P.crate(this, 0xb07a3a)];
      case "estanteria": return [P.shelving(this)];
      case "carretilla": return [P.machine(this, 0xe0a01a)];
      case "pale": return [P.pallet(this)];
      case "derrame": {
        const g = this.add.graphics();
        g.fillStyle(0x241f18, 0.7); g.fillEllipse(0, 2, 40, 18);
        g.fillStyle(0x3a3226, 0.6); g.fillEllipse(-5, 0, 22, 10);
        g.fillStyle(0x554a38, 0.35); g.fillEllipse(3, -1, 10, 5);
        return [g];
      }
      default: return super.buildInteractableArt(def);
    }
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x8a877e);
    const W = 1280, H = 720, over = 420;
    // techo/nave: gradiente frío de nave industrial
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0x9a978d, 0x9a978d, 0x7c7a72, 0x7c7a72, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.58 + over);
    // cerchas del techo (celosía metálica) sobre la nave
    const roof = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    roof.lineStyle(2, 0x6a6860, 0.7);
    const ry = H * 0.2;
    for (let cx = 40; cx < W; cx += 150) {
      roof.beginPath(); roof.moveTo(cx, ry); roof.lineTo(cx + 75, ry - 30); roof.lineTo(cx + 150, ry); roof.strokePath();
      roof.beginPath(); roof.moveTo(cx + 75, ry - 30); roof.lineTo(cx + 75, ry + 10); roof.strokePath();
    }
    roof.lineStyle(3, 0x6a6860, 0.6); roof.beginPath(); roof.moveTo(0, ry); roof.lineTo(W, ry); roof.strokePath();
    // paredes de la nave (chapa/hormigón)
    this.buildWalls({ left: 0x8f8c82, right: 0xa3a096, height: 176, base: 0x625f57, texture: "metal" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };

    // franjas de circulación (amarillas) marcando el carril de carretillas
    const g = this.add.graphics().setDepth(-9500);
    g.lineStyle(5, 0xe6c200, 0.7);
    for (const yy of [6.6, 7.4]) { const a = this.iso(9.5, yy), b = this.iso(this.grid.w - 1, yy); g.beginPath(); g.moveTo(a.sx, a.sy + TILE_H / 2); g.lineTo(b.sx, b.sy + TILE_H / 2); g.strokePath(); }
    this.decor.push(g);

    // estanterías de fondo (almacén lleno)
    put(P.shelving(this), 1.6, 1.4);
    put(P.shelving(this), 5.5, 1.4);
    put(P.shelving(this), 9.5, 1.4);
    put(P.shelving(this), 12.6, 1.4);
    put(P.gasrack(this), 13.4, 4.4);

    // palés, cajas y sacos por la nave (densidad)
    put(P.pallet(this), 2.4, 5.2);
    put(P.crateStack(this), 2.5, 4.7);
    put(P.crate(this, 0xc79a55), 7.4, 3.4);
    put(P.crate(this, 0xa9843f), 8.0, 3.7);
    put(P.sacks(this), 12.4, 8.4);
    put(P.sacks(this), 3.0, 8.6);
    put(P.barrel(this, 0x2f6b3f), 13.2, 6.6);
    put(P.barrel(this, 0xc4632a), 13.6, 7.0);
    put(P.wheelbarrow(this), 6.4, 9.4);
    put(P.toolbox(this, 0x2f6b8f), 1.8, 6.6);
    put(P.crateStack(this), 10.4, 2.2);
    put(P.cone(this), 7.2, 6.6);
    put(P.cone(this), 9.4, 8.2);
    put(P.fence(this), 4.2, 2.4);
    put(P.extinguisher(this), 1.4, 4.2);
    put(P.warningSign(this), 8.6, 5.4);

    // luminarias industriales colgantes + charco de luz (con pulso)
    for (const [lx, ly] of [[3.5, 3], [7.5, 3], [11.5, 3], [5.5, 6.5], [10.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2;
      pg.fillStyle(0xffe6a0, 0.13); pg.fillEllipse(p.sx, pcy, 82, 38);
      pg.fillStyle(0xfff4c8, 0.12); pg.fillEllipse(p.sx, pcy, 46, 22);
      this.decor.push(pg); this.addPulse(pg, 0.12, 0.004, 0.86);
      const lamp = P.hanglamp(this, 150); lamp.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(lamp); this.addSway(lamp);
    }

    // polvo suspendido cerca de los sacos (vida ambiental)
    this.addRisers(12.4, 8.4, { count: 3, color: 0xdcc7a4, alpha: 0.18, r: 3, rise: 26, sp: 0.004, grow: 1.5, dyBase: 6, depth: 30000 });
    this.addRisers(3.0, 8.6, { count: 3, color: 0xdcc7a4, alpha: 0.16, r: 3, rise: 24, sp: 0.005, grow: 1.4, dx: 3, dyBase: 6, depth: 30000 });

    if (this.state.chapter("poligono").done) this.onResolved();
  }

  protected onResolved(): void {
    // estantería anclada y paso despejado: señal de "zona ordenada"
    const c = this.iso(3, 3);
    this.add.text(c.sx, c.sy - 58, "CARGA MÁX. 300 kg/balda", { fontSize: "9px", color: "#241d14", backgroundColor: "#f0c400", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
    const d = this.iso(11, 6.9);
    this.add.text(d.sx, d.sy - 34, "PASO PEATONAL", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
  }
}
